import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {getInitials, getColorFromName} from '../../utils/helpers'
import {Typography, Avatar, Chip, Select, MenuItem, Skeleton} from '@mui/material'
import {SkeletonContainer, AvatarSkeleton, ChipSkeleton, TextSkeleton} from '../Skeletons'
import {Link, useLocation, useParams} from 'react-router-dom'
import {CheckCircle, CalendarToday, Person, ContactPage, SelfImprovement, Ballot, Assignment, Close} from '@mui/icons-material'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {clientApi} from '../../services'
import {parseError} from '../../utils/helpers'
import {format} from 'date-fns'
import {useMobile} from '../../hooks'
import {useNavigate} from 'react-router-dom'

export default () => {
    const [client, setClient] = useState(null)
    const [initials, setInitials] = useState(null)
    const [avatarColor, setAvatarColor] = useState(null)
    const {setMessageBox} = useGlobalStates()
    const [skeleton, setSkeleton] = useState(true)
    const [menuValue, setMenuValue] = useState('')
    const location = useLocation()
    const {clientId} = useParams()
    const {mobile} = useMobile()
    const navigate = useNavigate()

    useEffect(() => {
        setSkeleton(true)
        clientApi
            .getClientDetails(clientId)
            .then(({data: {data}}) => {
                setClient(data)
                setSkeleton(false)
            })
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [])

    useEffect(() => {
        if (!client?.avatar_url) {
            setInitials(getInitials(client?.first_name + ' ' + client?.last_name))
            setAvatarColor(getColorFromName(client?.first_name + ' ' + client?.last_name))
        }
    }, [client])

    useEffect(() => {
        if (location.pathname) {
            setMenuValue(location.pathname)
        }
    }, [location.pathname])

    const menuItems = [
        {label: 'Schedule', icon: <CalendarToday />, path: `/clients-management/${clientId}/schedule`},
        {label: 'Personal Information', icon: <Person />, path: `/clients-management/${clientId}/personal-information`},
        {label: 'Client Contacts', icon: <ContactPage />, path: `/clients-management/${clientId}/client-contacts`},
        {label: 'Assigned Caregiver', icon: <SelfImprovement />, path: `/clients-management/${clientId}/assigned-caregiver`},
        {label: 'Service Agreement', icon: <Ballot />, path: `/clients-management/${clientId}/service-agreement`},
        {label: 'Documents', icon: <Assignment />, path: `/clients-management/${clientId}/documents`},
    ]

    const isActive = path => {
        if (path === '/') {
            return location.pathname === '/'
        }
        return location.pathname.startsWith(path)
    }

    return (
        <>
            {mobile ? (
                <div className="mobile-container">
                    {client?.first_name ? (
                        <div className="profile-container">
                            <div className="profile">
                                {client?.avatar_url ? (
                                    <Avatar src={client?.avatar_url} style={{width: 56, height: 56}} />
                                ) : (
                                    <Avatar style={{backgroundColor: avatarColor, width: 56, height: 56, fontSize: 28}}>{initials}</Avatar>
                                )}
                                <div className="name-container">
                                    <Typography variant="h7" color="text.primary">
                                        {client?.first_name + ' ' + client?.last_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {client?.email}
                                    </Typography>
                                </div>
                            </div>
                            {client?.status == 'active' ? (
                                <Chip label="Active" color="success" variant="filled" />
                            ) : (
                                <Chip label="Deactivated" color="error" variant="filled" />
                            )}
                        </div>
                    ) : skeleton ? (
                        <SkeletonContainer className="profile-container">
                            <div className="profile">
                                <AvatarSkeleton size={56} />
                                <div className="name-container">
                                    <TextSkeleton width={130} fontSize={16} />
                                    <TextSkeleton width={170} />
                                </div>
                            </div>
                            <ChipSkeleton width={62} height={32} />
                        </SkeletonContainer>
                    ) : null}
                    <br />
                    <Select value={menuValue} onChange={e => navigate(e.target.value)} fullWidth>
                        {menuItems.map(item => (
                            <MenuItem key={item.path} value={item.path}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            ) : (
                <div className="container">
                    {client?.first_name ? (
                        <div className="profile">
                            {client?.avatar_url ? (
                                <Avatar src={client?.avatar_url} style={{width: 64, height: 64}} />
                            ) : (
                                <Avatar style={{backgroundColor: avatarColor, width: 64, height: 64, fontSize: 30}}>{initials}</Avatar>
                            )}
                            <Typography variant="h6" color="text.primary" className="name">
                                {client?.first_name + ' ' + client?.last_name}
                            </Typography>
                            <Typography variant="body1" color="text.primary" className="name">
                                {client?.email}
                            </Typography>
                            <div className="chip">
                                <Typography variant="caption" color="primary">
                                    CLIENT
                                </Typography>
                            </div>
                            {client?.status == 'active' ? (
                                <Chip label="Active" color="success" variant="filled" />
                            ) : (
                                <Chip label="Deactivated" color="error" variant="filled" />
                            )}
                            <div className="eligible">
                                <div className="eligible-top">
                                    <Typography variant="body2" color="text.primary" style={{fontWeight: 700}}>
                                        Eligibility:
                                    </Typography>
                                    {client?.eligibility?.status == 'eligible' ? (
                                        <CheckCircle color="success" fontSize="small" />
                                    ) : (
                                        <Close color="error" fontSize="small" />
                                    )}
                                </div>
                                <div className="eligible-bottom">
                                    <Typography variant="body2" color="text.primary" style={{fontWeight: 700}}>
                                        Eligible Check Date:
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {client?.eligibility?.last_check_date && format(new Date(client?.eligibility?.last_check_date), 'MM/dd/yyyy')}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    ) : skeleton ? (
                        <SkeletonContainer className="profile">
                            <AvatarSkeleton size={64} />
                            <TextSkeleton width={150} fontSize={20} />
                            <TextSkeleton width={190} fontSize={16} />
                            <Skeleton variant="rounded" width={50} height={24} sx={{borderRadius: 2}} />
                            <ChipSkeleton width={62} height={32} />
                            <Skeleton variant="rounded" width={250} height={62} sx={{borderRadius: 2}} />
                        </SkeletonContainer>
                    ) : null}
                    <br />
                    {menuItems.map(({label, icon, path}) => (
                        <div key={label} className={`item-container ${isActive(path) ? 'active' : ''}`}>
                            <Link to={path} className={`item ${isActive(path) ? 'active' : ''}`}>
                                <div className="icon">{icon}</div>
                                <div className="item-label">{label}</div>
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </>
    )
}
